<template>
  <div
    v-if="ready"
    :class="bem()"
  >
    <NuxtPage
      v-if="hasChildRoute"
      :categories="categories"
      :articles="articles"
    />

    <template v-else>
      <SupportBreadcrumbs
        :class="bem('breadcrumbs')"
        :breadcrumbs="breadcrumbs"
      />

      <div :class="bem('intro')">
        <div
          v-if="category.icon"
          :class="bem('icon-wrapper')"
        >
          <img
            class="w-14 h-14"
            :src="`https://intercom.help/sungod/assets/svg/icon:${category.icon}/3449B8`"
            alt=""
            width="100%"
            height="100%"
            loading="lazy"
          >
        </div>

        <div :class="bem('copy')">
          <VisionHeading
            tag="h3"
            size="h3"
            :class="bem('heading')"
          >
            <!-- eslint-disable-next-line vue/no-v-html -->
            <span v-html="category.label || category.name" />
          </VisionHeading>
          <p
            v-if="category.childArticleCount"
            :class="bem('count')"
          >
            {{ category.childArticleCount }} {{ $trp('article', 'articles', category.childArticleCount) }}
          </p>
        </div>
      </div>

      <div :class="bem('categories')">
        <SupportArticleList
          v-for="subCategory in category.categories"
          :key="subCategory.id"
          :category="subCategory"
        />

        <SupportArticleList
          v-if="category.articles.length"
          :category="{ articles: category.articles }"
        />
      </div>
    </template>
  </div>
</template>

<script setup>
const bem = useBEM('SupportCategoryPage')
const { resolveLocalePath } = useUrlBuilder()
const { runWithContext } = useNuxtApp()
const route = useRoute()

const ready = ref(false)

const props = defineProps({
  categories: { type: Array, required: true },
  articles: { type: Array, required: true }
})

const categoryId = route.params?.category?.split('-')?.[0]
const flatCategories = props.categories.flatMap((category) => [
  category,
  ...(category.categories || [])
])
const category = flatCategories.find(
  (category) => categoryId && category.id === categoryId
)

const hasChildRoute = computed(() => {
  return route.matched.length > 2
})

let breadcrumbs
if (!category){
  await navigateTo({
    path: resolveLocalePath('/support'),
    redirectCode: 302
  })
} else {
  definePageMeta({
    pageType: 'Support Category Page'
  })
  
  const { getTitle, getDescription } = useMetaHelper()
  // Don't include header or microdata if there is a child route
  if (!hasChildRoute.value) {
    runWithContext(() => {
      const title = getTitle(`${category.label || category.name} | Support`)
      useHead({
        title,
        ...getDescription("Questions? We're here to help!"),
        meta: [
          { hid: 'og:title', property: 'og:title', content: title },
        ]
      })
    
      useJsonld({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: category.articles.map((article) => ({
          '@type': 'Question',
          name: article.title,
          acceptedAnswer: {
            '@type': 'Answer',
            text: article.body
          }
        }))
      })
    })
  }
  
  breadcrumbs = computed(() => {
    const breadcrumbs = []
  
    // Find all parent categories
    let parentCategoryId = category.parent_id
    while (parentCategoryId) {
      const parentCategory = flatCategories.find(
        (category) => category.id === `${parentCategoryId}`
      )
      breadcrumbs.unshift({
        label: parentCategory.label || parentCategory.name,
        target: resolveLocalePath(parentCategory.link)
      })
  
      parentCategoryId = parentCategory.parent_id
    }
  
    // Add root support page
    breadcrumbs.unshift({
      label: 'Support',
      target: resolveLocalePath('/support')
    })
  
    // Add current page
    breadcrumbs.push({
      label: category.label || category.name,
      active: true
    })
  
    return breadcrumbs
  })
  
  onMounted(async () => {
    const { $track } = useNuxtApp()
    await $track.support.viewCategory(category)
  })

  ready.value = true
}

</script>

<style lang="postcss">
.SupportCategoryPage {
  @apply flex flex-col gap-8;
}

.SupportCategoryPage__intro {
  @apply flex flex-col gap-4;
}

.SupportCategoryPage__back-button {
  @apply flex gap-2;
}

.SupportCategoryPage__copy {
  @apply flex flex-col gap-2;
}

.SupportCategoryPage__categories {
  @apply flex flex-col gap-sm mt-sm;
}
</style>